import React, {
  ReactElement,
} from 'react';
import {
  Controller,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import type {
  FieldError,
} from 'react-hook-form';
import {
  getNumberFromValue,
  notEmptyString,
} from '@populateui/utils';
import ServicesLookup, {
  ExternalFilter,
} from 'views/EMR/components/ServiceLookup';
import {
  filterCodes,
  normalizeServiceCodeOption,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import type {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import Input from 'pages/Dashboard/components/Input';
import Select from 'core/components/Select';
import {
  PatientAuthorizationAllowanceBasis,
} from 'dtos/patientAuthorizationAllowanceBasis';
import type {
  AuthorizationAllowanceFormValue,
  AuthorizationFormValues,
} from 'views/EMR/components/Authorizations/formUtils';
import {
  validateAuthorizationAllowances,
} from 'views/EMR/components/Authorizations/formUtils';
import {
  useGetFavoriteCodes,
} from 'views/EMR/Settings/hooks';

const basisOptions = [
  PatientAuthorizationAllowanceBasis.Visits,
  PatientAuthorizationAllowanceBasis.Units,
];

type Props = {
  name?: `allowances`;
  disabled?: boolean;
};

function EmptyAllowanceContent() {
  return null;
}

function getAllowanceKey(
  allowance: AuthorizationAllowanceFormValue,
  index: number,
): string {
  return allowance.patientAuthorizationAllowanceId?.toString()
    ?? allowance.codes[0]?.code
    ?? `allowance-${index}`;
}

export default function AllowanceEditor({ name = 'allowances', disabled = false }: Props) {
  const {
    control,
    setValue,
    trigger,
  } = useFormContext<AuthorizationFormValues>();
  const allowanceValues = useWatch({
    control,
    name,
  }) as AuthorizationFormValues['allowances'];
  const allowances = allowanceValues ?? [];
  const allowanceBasis = allowances[0]?.allowanceBasis;
  const {
    data: favoriteCodes,
    refetch: refetchFavoriteCodes,
  } = useGetFavoriteCodes();

  const selectedServices: VisitNoteServiceCode[] = allowanceBasis
    === PatientAuthorizationAllowanceBasis.Units
    ? allowances.flatMap((allowance, index) => (
      allowance.codes.map((codeEntry, codeIndex) => ({
        code: codeEntry.code,
        description: codeEntry.description ?? undefined,
        numberOfUnits: getNumberFromValue(allowance.approvedQuantity) || undefined,
        serviceCodeDetails: {
          externalUsedQuantity: allowance.externalUsedQuantity,
        },
        __id: `${getAllowanceKey(allowance, index)}-${codeIndex}`,
      }))
    ))
    : [];

  const handleBasisChange = (option: string | string[]) => {
    if (disabled) {
      return;
    }

    const basis = Array.isArray(option) ? option[0] : option;
    if (!basisOptions.includes(basis as PatientAuthorizationAllowanceBasis)) {
      return;
    }

    if (basis === allowanceBasis) {
      return;
    }
    setValue(name, [{
      patientAuthorizationAllowanceId: allowances[0]?.patientAuthorizationAllowanceId,
      allowanceBasis: basis as PatientAuthorizationAllowanceBasis,
      approvedQuantity: undefined,
      externalUsedQuantity: 0,
      codes: [],
    }], {
      shouldDirty: true,
    });
  };

  const handleServicesChange = (services?: VisitNoteServiceCode[]) => {
    if (disabled) {
      return;
    }

    const existingByCode = new Map<string, AuthorizationAllowanceFormValue>();
    allowances.forEach((allowance) => {
      allowance.codes.forEach(({ code }) => {
        if (!existingByCode.has(code)) {
          existingByCode.set(code, allowance);
        }
      });
    });

    const usedAllowanceIds = new Set<number>();
    const nextAllowances = (services ?? []).flatMap((service) => {
      if (!service.code) {
        return [];
      }

      const existing = existingByCode.get(service.code);
      const existingId = existing?.patientAuthorizationAllowanceId;
      const patientAuthorizationAllowanceId = existingId && !usedAllowanceIds.has(existingId)
        ? existingId
        : undefined;
      if (patientAuthorizationAllowanceId) {
        usedAllowanceIds.add(patientAuthorizationAllowanceId);
      }

      return [{
        patientAuthorizationAllowanceId,
        allowanceBasis: PatientAuthorizationAllowanceBasis.Units,
        approvedQuantity: getNumberFromValue(service.numberOfUnits) || undefined,
        externalUsedQuantity: service.serviceCodeDetails?.externalUsedQuantity ?? 0,
        codes: [{
          code: service.code,
          description: service.description,
        }],
      }];
    });

    setValue(name, nextAllowances.length > 0 ? nextAllowances : [{
      patientAuthorizationAllowanceId: allowances[0]?.patientAuthorizationAllowanceId,
      allowanceBasis: PatientAuthorizationAllowanceBasis.Units,
      approvedQuantity: undefined,
      externalUsedQuantity: 0,
      codes: [],
    }], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const basisRenderers: Partial<Record<
    PatientAuthorizationAllowanceBasis,
    (validationError?: FieldError) => ReactElement
  >> = {
    [PatientAuthorizationAllowanceBasis.Visits]: (validationError) => (
      <>
        <Controller
          control={control}
          name={`${name}.0.approvedQuantity`}
          render={({ field: quantityField }) => (
            <Input
              {...quantityField}
              id="authorization-allowance-quantity"
              label="Approved"
              className="col-span-2"
              type="number"
              required
              inputProps={{ min: 1, step: 1, 'aria-invalid': Boolean(validationError) }}
              disabled={disabled}
              error={Boolean(validationError)}
              showErrorText={false}
              onChange={(value) => {
                quantityField.onChange(value);
                trigger(name);
              }}
            />
          )}
        />
        <Controller
          control={control}
          name={`${name}.0.externalUsedQuantity`}
          render={({ field }) => (
            <Input
              {...field}
              id="authorization-allowance-external-used-quantity"
              label="Already Used"
              className="col-span-1"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              disabled={disabled}
              showErrorText={false}
            />
          )}
        />
      </>
    ),
    [PatientAuthorizationAllowanceBasis.Units]: (validationError) => (
      <ServicesLookup
        id="authorization-allowance-codes"
        className="col-span-3"
        classes={{ root: 'w-full', wrapper: '!w-full' }}
        label="Service Codes"
        dropdownId="authorization-allowance-code-selection"
        type="CPTCode"
        placeholder="Search service codes"
        value={selectedServices}
        isAuthorizationAllowance
        externalFilter={filterCodes as ExternalFilter}
        normalizeOption={(option) => ({
          ...normalizeServiceCodeOption(option),
          numberOfUnits: undefined,
        })}
        favorites={favoriteCodes?.favoritedBillingCodes ?? []}
        refetchFavoriteCode={refetchFavoriteCodes}
        actions={['pin']}
        formEnabled={false}
        readonly={disabled}
        required
        error={Boolean(validationError) && allowances.some(({ codes }) => (
          codes.length !== 1 || !notEmptyString(codes[0]?.code)
        ))}
        onChange={handleServicesChange}
      />
    ),
  };

  return (
    <div className="col-span-2 grid grid-cols-3 items-start gap-3">
      <Controller
        control={control}
        name={`${name}.0.allowanceBasis`}
        rules={{ required: 'Select basis' }}
        render={({ field: basisField, fieldState: { error } }) => (
          <Select<string>
            id="authorization-allowance-basis"
            label="Basis"
            required
            className="col-span-3"
            onChange={(option) => {
              basisField.onChange(option);
              handleBasisChange(option);
            }}
            value={basisField.value ?? null}
            options={basisOptions}
            getLabel={(option) => option}
            getValue={(option) => option}
            onBlur={basisField.onBlur}
            disabled={disabled}
            error={Boolean(error)}
            showErrorText={false}
            muiClasses={{ select: '!text-left' }}
          />
        )}
      />
      <Controller
        control={control}
        name={name}
        rules={{ validate: (value) => validateAuthorizationAllowances(value) }}
        render={({ fieldState: { error } }) => (
          allowanceBasis
            ? basisRenderers[allowanceBasis]?.(error) ?? <EmptyAllowanceContent />
            : <EmptyAllowanceContent />
        )}
      />
    </div>
  );
}
